import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { timer } from 'rxjs';
import { AuthService } from '@services/auth.service';

type ResetView = 'waiting' | 'form' | 'success' | 'invalid' | 'oauth-only';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './reset-password.html',
  styleUrls: ['./reset-password.css'],
})
export class ResetPasswordPage implements OnInit, OnDestroy {
  view: ResetView   = 'waiting';
  password          = '';
  confirmPassword   = '';
  loading           = false;
  errorMsg          = '';
  oauthProvider     = '';   // set when the account is OAuth-only

  private _destroy$ = new Subject<void>();

  constructor(private auth: AuthService, private router: Router) {}

  ngOnInit(): void {
    const saved = localStorage.getItem('pxlvd-theme') as 'pxlvd' | 'dark' | 'light' | null;
    document.body.setAttribute('data-theme', saved ?? 'pxlvd');

    // Always subscribe to PASSWORD_RECOVERY - Supabase v2 PKCE flow puts ?code= in the
    // URL (not #type=recovery in the hash), so we can't distinguish recovery loads from
    // direct navigation via URL alone. recoveryMode$ is a BehaviorSubject; if the event
    // already fired we get true immediately on subscribe.
    this.auth.recoveryMode$.pipe(
      filter(v => v),
      take(1),
      takeUntil(this._destroy$),
    ).subscribe(() => this._onRecoveryReady());

    // Safety-net: act after auth initialises if PASSWORD_RECOVERY never fires.
    const timeoutMs = this.auth.isPasswordRecoveryLoad ? 6000 : 0;
    this.auth.state$.pipe(
      filter(s => s.initialized),
      take(1),
      switchMap(() => timer(timeoutMs)),
      takeUntil(this._destroy$),
    ).subscribe(() => {
      if (this.view !== 'waiting') return; // recovery event already handled it
      if (this.auth.isAuthenticated) {
        // Logged-in user who navigated here directly - just send them to the app silently.
        this._navigateBack('/');
      } else {
        // Not logged in, no recovery token - link is invalid or expired.
        this.view = 'invalid';
      }
    });
  }

  private _onRecoveryReady(): void {
    const user = this.auth.user;
    if (!user) { this.view = 'form'; return; }

    // Check whether this account was created with a social provider only (e.g. Google).
    // user.identities is an array with one entry per linked provider.
    const identities = (user as any).identities as Array<{ provider: string }> | undefined ?? [];
    const hasEmailIdentity = identities.some(i => i.provider === 'email');

    if (identities.length > 0 && !hasEmailIdentity) {
      // OAuth-only account - there is no password to reset.
      // The PKCE recovery link already signed the user in as a side-effect; sign them
      // out immediately so they aren't inadvertently left with an active session.
      const raw = identities[0].provider;               // e.g. 'google', 'github'
      this.oauthProvider = raw.charAt(0).toUpperCase() + raw.slice(1);  // 'Google'
      this.view = 'oauth-only';
      this.auth.signOut().catch(() => { /* best-effort */ });
    } else {
      this.view = 'form';
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  async onSubmit(): Promise<void> {
    this.errorMsg = '';
    if (!this.password) { this.errorMsg = 'Enter a new password.'; return; }
    if (this.password.length < 8) { this.errorMsg = 'Password must be at least 8 characters.'; return; }
    if (this.password !== this.confirmPassword) { this.errorMsg = 'Passwords do not match.'; return; }

    this.loading = true;
    const { error } = await this.auth.updatePassword(this.password);
    this.loading = false;

    if (error) {
      this.errorMsg = error.message.includes('weak') ? 'Choose a stronger password.' : error.message;
      return;
    }
    this.view = 'success';
  }

  private _navigateBack(fallback = '/'): void {
    const stored = sessionStorage.getItem('pxlvd-auth-return');
    sessionStorage.removeItem('pxlvd-auth-return');
    this.router.navigateByUrl(stored ?? fallback);
  }

  goToApp(): void {
    // After a successful reset the user is signed in - send them back to where they
    // were (e.g. pricing page) so they can continue what they started.
    this._navigateBack('/');
  }

  goToLogin(): void {
    // Navigate to the /login page. If a return URL is stored in sessionStorage (set by
    // the auth modal's onForgot before sending the reset email), LoginPage will redirect
    // there after a successful sign-in. Otherwise it falls back to the canvas root.
    this.router.navigate(['/login']);
  }
}
